import { auth } from "@/auth.js";
import api from "@/api";
import { isTransientAuthTokenError } from "@/types/errors/TransientAuthTokenError";
import { logWarn } from "@/helpers/logError";
import type { AuthHealthResponse } from "@/types/ExchangeAuthHealth";
import type { App, ManageableWorkflow } from "@/types/App";

// Build URLs lazily to avoid accessing window.env before it's initialized
const getBaseUrl = (): string => {
	const w = window as any;
	const env = (w && w.env) || {};
	const url = env.VITE_EXCHANGE_URL || "";
	return url;
};

const getPlatformCodeName = (): string => {
	const w = window as any;
	const env = (w && w.env) || {};
	const url = env.VITE_EXCHANGE_PLATFORM_CODE_NAME || "liveswitchliveswitchapi";
	return url;
};
// public/apps and publicapps both work, but the former is more consistent with the rest of the API
const getAppsUrl = (): string => getBaseUrl() + "/api/public/apps";

const getAuthHeaders = async (): Promise<any> => {
	let token = null;
	try {
		token = await auth.getAccessToken();
	} catch (e) {
		// LSP-17079: getAccessToken() now throws TransientAuthTokenError on a transient/offline flap.
		// Preserve the legacy null behavior — proceed with no Authorization header (the public apps
		// endpoint tolerates it) rather than failing the whole exchange request. Re-throw anything else.
		if (!isTransientAuthTokenError(e)) {
			throw e;
		}
		logWarn("exchange.getAuthHeaders: transient auth token failure; proceeding without Authorization header", {
			classification: "transient",
			retryable: true
		});
	}
	const headers: any = {
		"Content-Type": "application/json"
	};
	if (token) {
		headers["Authorization"] = "Bearer " + token;
	}
	return headers;
};

// Build a user query string with email + organizationId when available
const getUserQuery = async (): Promise<string> => {
	try {
		if (auth.runningCallback) {
			await auth.waitForCallbackToFinish();
		}
		const me = await api.getMe();
		const email: string | undefined = me.user?.email;
		const organizationId: string | undefined = me.organization?.id;
		const params = new URLSearchParams();
		if (email) params.set("email", email);
		if (organizationId) params.set("organizationId", organizationId);
		const qs = params.toString();
		return qs ? `?${qs}` : "";
	} catch (e) {
		console.error("Failed to get user query", e);
		return "";
	}
};

const getApps = async (): Promise<App[] | null> => {
	// query the exchange for any installable apps
	// which will include the ones that are already installed,
	// the ones that are publicly available but not installed, and the ones that are privately created
	// by this organization
	if (auth.runningCallback) {
		await auth.waitForCallbackToFinish();
	}
	const headers = await getAuthHeaders();
	const response = await fetch(getAppsUrl() + "/platform/by-name/" + getPlatformCodeName(), {
		method: "GET",
		headers: headers
	});
	if (response != null && response.ok) {
		return await response.json();
	}
	return null;
};

// watchConnected is a recursive function that checks if the window is closed,
// and if it is, it reloads the apps
const watchConnected = (w: Window | null, done: (value?: unknown) => void) => {
	if ((w && w.closed) || !w) {
		done();
	} else {
		setTimeout(() => {
			watchConnected(w, done);
		}, 1000);
	}
};

// NOTE: platform login/logout now go through scribe (see apps/contact-admin/src/api/scribe.ts
// getPlatformLoginUrl / logoutPlatform), unified with contact-async / contact-live. The former
// Exchange-based `login`/`logout` helpers were removed once the Sparky run/queue/template flows
// migrated to the shared usePackRequirements composable. The Apps/installations helpers below are
// a separate feature and still use Exchange.

const connect = async (appId: string, mode: "install" | "update" = "install"): Promise<any> => {
	// open the connect url in a new window
	const query = await getUserQuery();
	const separator = query ? "&" : "?";
	const connectUrl = getAppsUrl() + `/connect/${appId}` + query + `${separator}mode=${mode}`;
	const w = window.open(connectUrl, "_blank");
	const p = new Promise((resolve) => {
		watchConnected(w, resolve);
	});
	return p;
};

const activate = async (appId: string): Promise<any> => {
	const activateUrl = getAppsUrl() + `/connect/${appId}` + (await getUserQuery()) + "&authLevel=user";
	const w = window.open(activateUrl, "_blank");
	const p = new Promise((resolve) => {
		watchConnected(w, resolve);
	});
	return p;
};

const disconnect = async (appId: string): Promise<any> => {
	const disconnectUrl = getAppsUrl() + "/uninstall/" + appId + (await getUserQuery());
	const headers = await getAuthHeaders();
	await fetch(disconnectUrl, {
		method: "POST",
		headers: headers
	});
};

const deactivate = async (appId: string): Promise<any> => {
	const deactivateUrl = getAppsUrl() + "/deactivate/" + appId + (await getUserQuery());
	const headers = await getAuthHeaders();
	await fetch(deactivateUrl, {
		method: "POST",
		headers: headers
	});
};

const getAuthHealth = async (): Promise<AuthHealthResponse | null> => {
	if (auth.runningCallback) {
		await auth.waitForCallbackToFinish();
	}
	const headers = await getAuthHeaders();
	try {
		const response = await fetch(getBaseUrl() + "/api/public/apps/platform/failing-auths", {
			method: "GET",
			headers: headers
		});
		if (response != null && response.ok) {
			return await response.json();
		}
		return null;
	} catch (e) {
		console.error("Failed to fetch auth health", e);
		return null;
	}
};

const getManageableWorkflows = async (installationId: string): Promise<ManageableWorkflow[] | null> => {
	if (auth.runningCallback) {
		await auth.waitForCallbackToFinish();
	}
	const headers = await getAuthHeaders();
	const response = await fetch(getAppsUrl() + `/installations/${installationId}/workflows/manageable`, {
		method: "GET",
		headers
	});
	if (response.ok) {
		return await response.json();
	}
	return null;
};

const enableOptionalWorkflows = async (installationId: string, workflowIds: string[]): Promise<ManageableWorkflow[] | null> => {
	if (auth.runningCallback) {
		await auth.waitForCallbackToFinish();
	}
	const headers = await getAuthHeaders();
	const response = await fetch(getAppsUrl() + `/installations/${installationId}/workflows/enable`, {
		method: "POST",
		headers,
		body: JSON.stringify({ workflowIds })
	});
	if (response.ok) {
		return await response.json();
	}
	return null;
};

const disableOptionalWorkflows = async (installationId: string, workflowIds: string[]): Promise<ManageableWorkflow[] | null> => {
	if (auth.runningCallback) {
		await auth.waitForCallbackToFinish();
	}
	const headers = await getAuthHeaders();
	const response = await fetch(getAppsUrl() + `/installations/${installationId}/workflows/disable`, {
		method: "POST",
		headers,
		body: JSON.stringify({ workflowIds })
	});
	if (response.ok) {
		return await response.json();
	}
	return null;
};

export {
	getApps,
	connect,
	disconnect,
	activate,
	deactivate,
	getAuthHealth,
	watchConnected,
	getManageableWorkflows,
	enableOptionalWorkflows,
	disableOptionalWorkflows
};
